import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useScrollToIcon } from '../../hooks/useScrollToIcon';

interface ScrollableIconProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  position: 'left' | 'right';
  isScrollToTop?: boolean; // New prop to identify scroll-to-top icon
}

export const ScrollableIcon: React.FC<ScrollableIconProps> = ({
  id,
  children,
  className,
  style,
  position,
  isScrollToTop = false, // Default to false
}) => {
  const { scrollToIcon } = useScrollToIcon();
  const [bottomPosition, setBottomPosition] = useState(16); // Default bottom position
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const footerObserverRef = useRef<IntersectionObserver | null>(null);
  const lastUrlRef = useRef(window.location.href);

  // Safety buffer to prevent overlap with the footer during fast scrolling
  const safetyBuffer = 16;

  // Additional offset for scroll-to-top icon to position it higher
  const scrollToTopOffset = isScrollToTop ? 160 : 0;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    scrollToIcon(id);
  };

  // Function to calculate position relative to footer - memoized with useCallback
  const calculatePosition = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      // Find the footer element
      const footer =
        document.querySelector('footer') ||
        document.getElementById('footer') ||
        document.querySelector('.footer');

      if (!footer) {
        setBottomPosition(16 + scrollToTopOffset); // Default position with possible offset
        return;
      }

      // Get footer position info
      const footerRect = footer.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // If footer is not visible in viewport, use default position
      if (footerRect.top >= viewportHeight) {
        setBottomPosition(16 + scrollToTopOffset);
        return;
      }

      // Calculate how much of the footer is visible
      const footerVisibleAmount = Math.min(footerRect.height, viewportHeight - footerRect.top);

      // Set position to stay above the visible part of the footer
      setBottomPosition(footerVisibleAmount + safetyBuffer + scrollToTopOffset);
    });
  }, [safetyBuffer, scrollToTopOffset]);

  // Check for route changes
  useEffect(() => {
    const handleRouteChange = () => {
      if (window.location.href !== lastUrlRef.current) {
        lastUrlRef.current = window.location.href;
        // Reset position immediately and recalculate
        setBottomPosition(16 + scrollToTopOffset);
        setTimeout(calculatePosition, 100);
      }
    };

    // Set up route change detection using MutationObserver
    const observer = new MutationObserver(() => {
      handleRouteChange();
    });

    // Observe document for changes
    observer.observe(document, { subtree: true, childList: true });

    // Also listen for popstate events (browser back/forward)
    window.addEventListener('popstate', () => {
      setTimeout(calculatePosition, 100);
    });

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', calculatePosition);
    };
  }, [scrollToTopOffset, calculatePosition]);

  // Set up IntersectionObserver for the footer
  useEffect(() => {
    if (!isPageLoaded) return;

    const footer =
      document.querySelector('footer') ||
      document.getElementById('footer') ||
      document.querySelector('.footer');

    if (!footer) return;

    // Create observer options with margin to detect footer before it's visible
    const options = {
      rootMargin: '100px 0px 0px 0px', // Detect footer 100px before it enters viewport
      threshold: [0, 0.25, 0.5, 0.75, 1.0],
    };

    footerObserverRef.current = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        calculatePosition();
      }
    }, options);

    footerObserverRef.current.observe(footer);

    return () => {
      if (footerObserverRef.current) {
        footerObserverRef.current.disconnect();
      }
    };
  }, [isPageLoaded, scrollToTopOffset, calculatePosition]);

  // Set up scroll and resize listeners
  useEffect(() => {
    // Wait for page to load before setting up scroll listeners
    const handlePageLoad = () => {
      calculatePosition();
      setTimeout(() => {
        setIsPageLoaded(true);
      }, 300);
    };

    if (document.readyState === 'complete') {
      handlePageLoad();
    } else {
      window.addEventListener('load', handlePageLoad);
    }

    return () => {
      window.removeEventListener('load', handlePageLoad);
    };
  }, [scrollToTopOffset, calculatePosition]);

  // Add scroll and resize listeners after page is loaded
  useEffect(() => {
    if (!isPageLoaded) return;

    // Initial calculation
    calculatePosition();

    // Create scroll handler that detects direction
    let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    let scrollDirection = 0;

    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;

      // Detect scroll direction
      if (st > lastScrollTop) {
        scrollDirection = 1; // downward scroll
      } else if (st < lastScrollTop) {
        scrollDirection = -1; // upward scroll
      }

      lastScrollTop = st;

      // Prioritize downward scrolling (toward footer)
      if (scrollDirection === 1) {
        calculatePosition();
      } else {
        calculatePosition();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', calculatePosition, { passive: true });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isPageLoaded, scrollToTopOffset, calculatePosition]);

  // Check visibility settings from localStorage
  const showLeft = parseInt(localStorage.getItem('show_scrollable_icon_left') || '1', 10) === 1;
  const showRight = parseInt(localStorage.getItem('show_scrollable_icon_right') || '1', 10) === 1;

  // Return null if the icon should be hidden based on position
  if ((position === 'left' && !showLeft) || (position === 'right' && !showRight)) {
    return null;
  }

  // Add additional spacing if showLeft is true
  const additionalSpacing = position === 'left' ? 16 : 0;
  const finalBottomPosition = bottomPosition + additionalSpacing;

  return (
    <button
      onClick={handleClick}
      className={className}
      style={{
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        bottom: `${finalBottomPosition}px`,
        left: '16px', // Always position on the left side
        right: 'auto', // Ensure right is not set
        zIndex: 9999,
        pointerEvents: 'auto',
        opacity: isPageLoaded ? 1 : 0,
        transition: 'opacity 0.3s ease',
        ...style,
      }}
      aria-label={`Scroll to ${position}`}
    >
      {children}
    </button>
  );
};
